import { useMemo } from 'react';

import { useAuth } from '../auth';
import type { User } from '../auth/types';
import {
  getModuleIdForPermissionModuleName,
  getPermissionModuleNames,
  isDeclaredPermissionModuleName,
} from '../config/modules.config';

/**
 * The check itself, as a plain function over a `User`.
 *
 * Exported ONLY for a caller that cannot use the hooks below because it must not
 * throw when no AuthProvider is mounted — a shared component rendered outside the
 * app shell, gating an optional affordance. Everything else goes through
 * {@link useIsModuleAdmin} or {@link useModuleAdminCheck}; this file stays the
 * single place the three admit paths are spelled out (enforced by
 * `tools/check_module_admin_check_single_source.py`).
 *
 * The two grant lists are keyed in DIFFERENT namespaces and cannot be compared
 * to the same string — the trap this function absorbs so no caller has to:
 *
 * - `module_access_levels` is keyed by module config **id** (`ai`, `master_data`);
 * - `module_admin_roles` carries the **permission-module name** behind
 *   `{name}.settings.manage` (`ai_agents` for the `ai` module, `products` and
 *   `contacts` for `master_data`).
 *
 * Comparing one argument against both is wrong for every module whose spellings
 * diverge, and wrong in a silent, opposite direction on each list. So the
 * argument is normalised per list here, and callers may pass whichever spelling
 * they hold: a config id, the config's folder key, or a declared permission name.
 *
 * **Which spelling you pass decides how WIDE the question is**, for the modules
 * administered in sub-scopes (today only `master_data`, whose `products` and
 * `contacts` scopes are separately grantable):
 *
 * - a config id or folder key asks the module-WIDE question — admin of ANY of
 *   its scopes — which is what the sidebar and the module picker want;
 * - a declared permission-module name asks for THAT scope alone, which is what a
 *   settings surface behind `require_module_admin("contacts")` wants. A holder
 *   of `products.settings.manage` must not pass it, or the page renders controls
 *   the API can only answer 403 to.
 *
 * The T11 arm is deliberately outside that distinction: `module_access_levels` is
 * keyed by module id and has no sub-scope spelling, so a per-module `admin` level
 * is the umbrella over every scope the module has.
 */
export function isModuleAdminForUser(user: User | null, module: string): boolean {
  if (!user) return false;

  if (user.is_admin === true) return true;

  const moduleId = getModuleIdForPermissionModuleName(module);
  if (user.module_access_levels?.[moduleId] === 'admin') return true;

  const permissionNames = isDeclaredPermissionModuleName(module)
    ? [module]
    : getPermissionModuleNames(moduleId);
  return user.module_admin_roles?.some((name) => permissionNames.includes(name)) === true;
}

/**
 * Mirrors the backend `require_module_admin(module)` dependency.
 *
 * A route behind that gate admits three principals: a tenant admin, a holder of
 * the `{module}.settings.manage` permission via a role, or a user whose T11
 * per-module access level for the module is `admin`. A page that renders a CTA
 * for such a route must run the SAME three-way check, or a plain member sees a
 * button that can only ever answer 403.
 *
 * `module` may be the module's config id, its folder key, or one of its declared
 * permission-module names (`ModuleConfig.permissionModules`) — {@link
 * isModuleAdminFor} normalises them, and the choice decides whether the question
 * is module-wide or scoped to one admin sub-scope.
 *
 * Generalizes the check the sales Reports page spells out inline for its
 * commission tab; new callers should use this rather than re-typing it.
 */
export function useIsModuleAdmin(module: string): boolean {
  const { user } = useAuth();

  return isModuleAdminForUser(user, module);
}

/**
 * The same check, as a predicate over MANY modules — for a surface that filters
 * a list of modules or nav items in one pass (the sidebar, the module picker,
 * global search) and so cannot call a per-module hook.
 *
 * The returned function is referentially stable for a given user, so it is safe
 * in a `useMemo` dependency list.
 */
export function useModuleAdminCheck(): (module: string) => boolean {
  const { user } = useAuth();

  return useMemo(() => (module: string) => isModuleAdminForUser(user, module), [user]);
}
